import {
    AENDERBARE_FELDER,
    type AenderbaresFeld,
    type AufgeloesterKatalog,
    type KatalogEintrag,
    type KatalogKnoten,
    type KatalogOverlay,
} from './typen';
import { felderNormalisieren } from './ueberlagerung';

/**
 * Admin-Logik des Katalog-Editors, rein funktional: jede Funktion liefert
 * ein NEUES Overlay, das übergebene bleibt unverändert (auch seine Listen
 * und Unterobjekte).
 */

export const OVERLAY_WARNGRENZE_BYTES = 65536;

type Felder = Partial<Pick<KatalogKnoten, AenderbaresFeld>>;

/** Flache Kopie mit eigenen Listen/Objekten der ersten Ebene. */
function kopie(o: KatalogOverlay): KatalogOverlay {
    const k: KatalogOverlay = {
        format: 1,
        ausgeblendet: [...(o.ausgeblendet ?? [])],
        geaendert: { ...(o.geaendert ?? {}) },
        eigene: [...(o.eigene ?? [])],
        reihenfolge: { ...(o.reihenfolge ?? {}) },
    };
    if (o.gesehenBisVersion !== undefined) k.gesehenBisVersion = o.gesehenBisVersion;
    if (o.nachHaeufigkeit === false) k.nachHaeufigkeit = false;
    return k;
}

const istEigen = (o: KatalogOverlay, id: string): boolean => (o.eigene ?? []).some((k) => k.id === id);

export function knotenAusblenden(o: KatalogOverlay, id: string, aus: boolean): KatalogOverlay {
    const n = kopie(o);
    const ohne = n.ausgeblendet.filter((x) => x !== id);
    n.ausgeblendet = aus ? [...ohne, id] : ohne;
    return n;
}

/**
 * Felder ändern (nur AENDERBARE_FELDER). Eigene Knoten werden direkt
 * geändert, Standardknoten bekommen einen Eintrag in `geaendert` (mit
 * vorhandenen Änderungen zusammengeführt). Ein Feld mit `undefined` nimmt die
 * Änderung zurück (am eigenen Knoten: entfernt es; `kurz` bleibt Pflicht).
 */
export function knotenAendern(o: KatalogOverlay, id: string, felder: Felder, version: number): KatalogOverlay {
    const n = kopie(o);
    const entfernen = AENDERBARE_FELDER.filter((f) => Object.prototype.hasOwnProperty.call(felder ?? {}, f) && felder[f] === undefined);
    const setzen = felderNormalisieren(felder);

    if (istEigen(n, id)) {
        n.eigene = n.eigene.map((k) => {
            if (k.id !== id) return k;
            const neu: KatalogKnoten = { ...k, ...setzen };
            for (const f of entfernen) if (f !== 'kurz') delete neu[f];
            return neu;
        });
        return n;
    }

    const bisher: Felder = { ...(n.geaendert[id]?.felder ?? {}), ...setzen };
    for (const f of entfernen) delete bisher[f];
    if (Object.keys(bisher).length === 0) {
        delete n.geaendert[id];
    } else {
        n.geaendert[id] = { rev: version, felder: bisher };
    }
    return n;
}

/** Nimmt alle Feldänderungen eines Standardknotens zurück (Ausblenden bleibt). */
export function aufStandardZuruecksetzen(o: KatalogOverlay, id: string): KatalogOverlay {
    const n = kopie(o);
    delete n.geaendert[id];
    return n;
}

/** Legt einen eigenen Knoten an (id = 'eigen_' + uuid, vergibt der Aufrufer). Vorhandene ID → unverändert. */
export function eigenenKnotenAnlegen(o: KatalogOverlay, k: Omit<KatalogKnoten, 'id'>, id: string): KatalogOverlay {
    const n = kopie(o);
    if (!id || istEigen(n, id)) return n;
    const knoten: KatalogKnoten = { ...k, id, eltern: k.typ === 'kategorie' ? null : k.eltern };
    n.eigene = [...n.eigene, knoten];
    return n;
}

/** Löscht einen eigenen Knoten samt eigener Kinder und aller Verweise darauf. */
export function eigenenKnotenLoeschen(o: KatalogOverlay, id: string): KatalogOverlay {
    const n = kopie(o);
    if (!istEigen(n, id)) return n;
    const weg = new Set<string>([id]);
    let gewachsen = true;
    while (gewachsen) {
        gewachsen = false;
        for (const k of n.eigene) {
            if (!weg.has(k.id) && k.eltern !== null && weg.has(k.eltern)) {
                weg.add(k.id);
                gewachsen = true;
            }
        }
    }
    n.eigene = n.eigene.filter((k) => !weg.has(k.id));
    n.ausgeblendet = n.ausgeblendet.filter((x) => !weg.has(x));
    for (const x of weg) delete n.geaendert[x];
    const reihenfolge: Record<string, string[]> = {};
    for (const [eltern, liste] of Object.entries(n.reihenfolge)) {
        if (weg.has(eltern)) continue;
        const rest = liste.filter((x) => !weg.has(x));
        if (rest.length) reihenfolge[eltern] = rest;
    }
    n.reihenfolge = reihenfolge;
    return n;
}

/** Kachel-Kurztext aus einer Überschrift: Teil nach dem letzten „/“, höchstens 28 Zeichen (sonst mit „…“). */
export function kurzAusTitel(titel: string): string {
    const teil = (titel ?? '').replace(/^\s*\*+\s*/, '').split('/').pop()?.trim() || (titel ?? '').trim();
    if (teil.length <= 28) return teil;
    // Am Wortende kürzen, solange davon genug übrig bleibt.
    const roh = teil.slice(0, 27);
    const luecke = roh.lastIndexOf(' ');
    return (luecke >= 10 ? roh.slice(0, luecke) : roh).trimEnd() + '…';
}

/**
 * Holt einen Eintrag des Gesamtkatalogs als eigenen Mangel in die Torte: NUR
 * Inline-Texte (Überschrift, Beschreibung, gekürzte Vorschrift) — kein
 * Verweis auf den Eintrag, keine Nummer (Entscheid 28.09.). Liegt die
 * englische Fassung des Eintrags vor (28.09.), kommen Kurztext, Titel und
 * Text auch englisch mit (kurzEn/titelEn/textEn); die Vorschrift wird zur
 * Laufzeit übertragen.
 */
export function katalogEintragUebernehmen(
    o: KatalogOverlay,
    elternId: string,
    e: KatalogEintrag,
    id: string,
    englisch?: Pick<KatalogEintrag, 'titel' | 'text'> | null,
): KatalogOverlay {
    const knoten: Omit<KatalogKnoten, 'id'> = {
        typ: 'mangel',
        eltern: elternId,
        kurz: kurzAusTitel(e.titel) || kurzAusTitel(e.text) || 'Mangel',
        titel: e.titel,
        text: e.text,
        vorschrift: e.vorschrift,
    };
    if (englisch && (englisch.titel?.trim() || englisch.text?.trim())) {
        const kurzEn = kurzAusTitel(englisch.titel ?? '') || kurzAusTitel(englisch.text ?? '');
        if (kurzEn) knoten.kurzEn = kurzEn;
        if (englisch.titel?.trim()) knoten.titelEn = englisch.titel;
        if (englisch.text?.trim()) knoten.textEn = englisch.text;
    }
    return eigenenKnotenAnlegen(o, knoten, id);
}

/**
 * Verschiebt einen Knoten unter seinen Geschwistern um eine Stelle
 * (Reihenfolge laut `katalog`). Am Rand → unverändert. Die neue Reihenfolge
 * steht vollständig in overlay.reihenfolge[eltern]; dort gemerkte, im
 * Katalog nicht sichtbare IDs bleiben hinten erhalten.
 */
export function verschieben(o: KatalogOverlay, katalog: AufgeloesterKatalog, id: string, richtung: -1 | 1): KatalogOverlay {
    const n = kopie(o);
    const k = katalog.knoten.get(id);
    if (!k) return n;
    const eltern = k.eltern ?? '';
    const geschwister = [...(katalog.kinder.get(eltern) ?? [])];
    const i = geschwister.indexOf(id);
    const j = i + richtung;
    if (i < 0 || j < 0 || j >= geschwister.length) return n;
    [geschwister[i], geschwister[j]] = [geschwister[j], geschwister[i]];
    const menge = new Set(geschwister);
    const unsichtbar = (n.reihenfolge[eltern] ?? []).filter((x) => !menge.has(x));
    n.reihenfolge[eltern] = [...geschwister, ...unsichtbar];
    return n;
}

/** Automatische Reihenfolge nach Häufigkeit an/aus (gespeichert wird nur „aus“). */
export function haeufigkeitSortierungSetzen(o: KatalogOverlay, an: boolean): KatalogOverlay {
    const n = kopie(o);
    if (an) delete n.nachHaeufigkeit;
    else n.nachHaeufigkeit = false;
    return n;
}

export function alsGesehenMarkieren(o: KatalogOverlay, version: number): KatalogOverlay {
    const n = kopie(o);
    n.gesehenBisVersion = Math.max(o.gesehenBisVersion ?? 0, Number.isFinite(version) ? version : 0);
    return n;
}

/** Größe des gespeicherten JSON in Bytes (UTF-8). */
export function overlayGroesseBytes(o: KatalogOverlay): number {
    try {
        return new TextEncoder().encode(JSON.stringify(o)).length;
    } catch {
        return 0;
    }
}
